import * as authRequests from "@/helpers/api/auth";
import {
  getToken,
  removeToken,
  saveToken,
  getRegistrationSessionId,
  saveRegistrationSessionId,
  removeRegistrationSessionId,
} from "@/helpers/jwtHelper";
import Axios from "axios";
import router from "@/router";
import { getUserByIdRequest } from "@/helpers/api/user";
import { YMUserIdMetcrik, YMLoginMetrik } from "@/plugins/yandex_metrika";
import * as Sentry from "@sentry/vue";
import { devSkipCaptcha } from "@/helpers/devFlags";
// import {registrationRequest} from "@/helpers/api/auth";
import { generateUUID } from "@/helpers/uuid";
import {
  decideResumeRoute,
  readHybridFlag,
  writeHybridFlag,
  clearHybridFlag,
  writeHybridSession,
  readHybridSession,
  clearHybridSession,
} from "@/helpers/resumeRoute";
import {
  mapMaxLoginStatus,
  MAX_HYBRID_FLAG_TIMEOUT_MS,
} from "@/helpers/auth/maxLoginStatus";

/** Извлекает resume_token из resume_web_url или oppen:// resume_app_url */
function extractResumeToken(resumeUrl) {
  if (!resumeUrl || typeof resumeUrl !== "string") {
    return null;
  }
  const match = resumeUrl.match(/[?&]resume_token=([^&#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function extractResumeTokenFromPollData(pollData) {
  const fromWeb = extractResumeToken(pollData?.resume_web_url);
  if (fromWeb) {
    return fromWeb;
  }
  return extractResumeToken(pollData?.resume_app_url);
}

const RESUME_TOKEN_MIN_LEN = 20;
const RESUME_TOKEN_MAX_LEN = 50;
const HYBRID_RECOVERY_SESSION_KEY = "hybrid_recovery_session";
const RECOVERY_DEAD_PHRASE = "Ссылка не работает.";

// Второй «Далее» ждёт тот же POST, пока первый не завершился (D-163, D-164)
let recoveryStartPromise = null;

function readHybridRecoverySession() {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    const raw = localStorage.getItem(HYBRID_RECOVERY_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeHybridRecoverySession(session) {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(HYBRID_RECOVERY_SESSION_KEY, JSON.stringify(session));
}

function newRecoveryScreenId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return generateUUID();
}

function hasRecoverySecrets(data) {
  return Boolean(data && data.temp_token && data.poll_key);
}

function swapRecoverySecrets(session, data) {
  // openedAt копируем как есть: тихая подмена не начинает 60 секунд заново
  return {
    ...session,
    tempToken: data.temp_token,
    pollKey: data.poll_key,
    maxBotDeeplink: data.max_bot_deeplink || session.maxBotDeeplink || null,
    maxBotWebDeeplink:
      data.max_bot_web_deeplink || session.maxBotWebDeeplink || null,
    smsAvailableAt: data.sms_available_at || session.smsAvailableAt || null,
    openedAt: session.openedAt,
    linkReopen: Boolean(data.link_reopen),
  };
}

function leaveHybridRecovery(session) {
  return {
    phone: session && session.phone ? session.phone : "",
    tempToken: null,
    pollKey: null,
    maxBotDeeplink: null,
    maxBotWebDeeplink: null,
    smsAvailableAt: null,
    openedAt: null,
    step: null,
    screenId: null,
    deadPhrase: RECOVERY_DEAD_PHRASE,
  };
}

const state = {
  token: getToken(),
  tempToken: null,
  hybridRegistration: null,
  hybridPollAwaitingResumeLink: false,
  hybridRegistrationExpired: false,
  currentUser: null,
  captchaUrl: null,
  captcha: null,
  registrationSessionId: getRegistrationSessionId(),
  maxLoginEnabled: false,
  hybridRecovery: null,

  loginForm: {
    phone: {
      value: "",
      error: null,
    },
    password: {
      value: "",
      error: null,
    },
  },
  registrationForm: {
    status: 0,
    phone: {
      value: "",
      error: null,
    },
    code: {
      value: "",
      error: null,
    },
    searchCity: {
      value: "",
      error: null,
    },
    city: {
      value: null,
      error: null,
    },
    searchObject: {
      value: "",
      error: null,
    },
    object: {
      value: null,
      error: null,
    },
    account: {
      value: null,
      error: null,
    },
    password: {
      value: "",
      error: null,
    },
    repeatPassword: {
      value: "",
      error: null,
    },
  },
  recoveryForm: {
    status: 0,

    phone: {
      value: "",
      error: null,
    },
    code: {
      value: "",
      error: null,
    },
    password: {
      value: "",
      error: null,
    },
    repeatPassword: {
      value: "",
      error: null,
    },
  },
};

const getters = {
  getTempToken: (state) => state.tempToken,
  getHybridRegistration: (state) => state.hybridRegistration,
  isHybridRegistrationActive: (state) =>
    state.hybridRegistration?.verification_mode === "max_hybrid" &&
    !!state.hybridRegistration?.max_bot_deeplink,
  hybridPollAwaitingResumeLink: (state) => state.hybridPollAwaitingResumeLink,
  hybridRegistrationExpired: (state) => state.hybridRegistrationExpired,
  getLoginForm: (state) => state.loginForm,
  getRegistrationForm: (state) => state.registrationForm,
  getRecoveryForm: (state) => state.recoveryForm,
  getHybridRecovery: (state) =>
    state.hybridRecovery || readHybridRecoverySession(),
  isAuthorized: (state) => !!state.token,
  maxLoginEnabled: (state) => state.maxLoginEnabled,
  getCurrentUser: (state) => state.currentUser,
  getCaptcha: (state) => state.captchaUrl,
  getCaptchaValue: (state) => state.captcha,
  getUserId: (state) => (state.token ? state.token.user : null),
  getRegistrationSessionId: (state) => state.registrationSessionId,
};

const actions = {
  async fetchCaptcha(context) {
    try {
      const response = await Axios.post("/captcha/", {
        phone: context.state.registrationForm.phone.value
          .replace(/[^\d+]/g, "")
          .slice(0, 13),
      });
      context.commit("setCaptcha", response.data);
    } catch (error) {
      console.error("Error fetching captcha:", error);
    }
  },
  async login(context) {
    return new Promise((resolve, reject) => {
      authRequests
        .loginRequest({
          phone: context.state.loginForm.phone.value
            .replace(/[^\d+]/g, "")
            .slice(0, 13),
          password: context.state.loginForm.password.value,
        })
        .then((response) => {
          const { token, user } = response.data;
          context.commit("setToken", { token: { ...token, user: user.id } });
          context.commit("setUser", { user });
          YMLoginMetrik("login");
          context.commit("clearHybridRegistration");
          resolve();
          router.push("/");
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  async fetchMaxLoginFlag(context) {
    let timeoutId;
    const request = authRequests.maxHybridFlagRequest();
    // Таймаут выигрывает гонку, а поздний reject запроса не должен всплыть
    request.catch(() => {});
    const timeout = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error("max-hybrid-flag-timeout"));
      }, MAX_HYBRID_FLAG_TIMEOUT_MS);
    });
    try {
      const response = await Promise.race([request, timeout]);
      const enabled = response?.data?.enabled === true;
      context.commit("setMaxLoginEnabled", enabled);
      return enabled;
    } catch {
      context.commit("setMaxLoginEnabled", false);
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  },
  async startMaxLogin(context, replace) {
    const response = await authRequests.maxLoginStartRequest(replace || {});
    return response.data;
  },
  async pollMaxLogin(context, payload) {
    const login_token = payload && payload.login_token;
    const poll_key = payload && payload.poll_key;
    try {
      const response = await authRequests.maxLoginStatusRequest({
        login_token,
        poll_key,
      });
      return mapMaxLoginStatus({
        httpStatus: response.status,
        data: response.data,
        networkError: false,
      });
    } catch (error) {
      return mapMaxLoginStatus({
        httpStatus: error?.response?.status,
        data: error?.response?.data,
        networkError: !error?.response,
      });
    }
  },
  async cancelMaxLogin(context, payload) {
    const login_token = payload && payload.login_token;
    const poll_key = payload && payload.poll_key;
    try {
      await authRequests.maxLoginCancelRequest({ login_token, poll_key });
    } catch {
      // Отмена best-effort: ошибка сети не блокирует возврат на форму
    }
  },
  async exchangeMaxLoginCode(context, code) {
    try {
      const response = await authRequests.maxLoginExchangeRequest({
        exchange_code: code,
      });
      if (!response || response.status !== 200) {
        return false;
      }
      const token = response.data && response.data.token;
      const user = response.data && response.data.user;
      if (!token || !user) {
        return false;
      }
      // Навигацию делает beforeEnter: push из guard, который ещё и abort, гасит переход.
      await context.dispatch("applyMaxLoginSession", {
        token,
        user,
        navigate: false,
      });
      return true;
    } catch {
      return false;
    }
  },
  applyMaxLoginSession(context, { token, user, navigate = true }) {
    context.commit("setToken", { token: { ...token, user: user.id } });
    context.commit("setUser", { user });
    context.commit("clearHybridRegistration");
    if (navigate) {
      router.push("/");
    }
  },
  async startRecovery(context, type) {
    if (recoveryStartPromise) {
      return recoveryStartPromise;
    }
    const recoveryForm = context.getters.getRecoveryForm;
    recoveryStartPromise = authRequests
      .passwordResetRequest({
        phone: recoveryForm.phone.value.replace(/[^\d+]/g, "").slice(0, 13),
        code_type: type ? type.type : "phone_numbers",
        supports_max_hybrid: true,
      })
      .then((response) => {
        const data = response.data || {};
        if (data.verification_mode === "max_hybrid") {
          // Ожидание рисует план 11 из геттера. На CodeRecovery не уходим (D-165)
          context.commit("setHybridRecovery", {
            phone: recoveryForm.phone.value,
            tempToken: data.temp_token,
            pollKey: data.poll_key,
            maxBotDeeplink: data.max_bot_deeplink || null,
            maxBotWebDeeplink: data.max_bot_web_deeplink || null,
            smsAvailableAt: data.sms_available_at || null,
            openedAt: Date.now(),
            step: "wait",
            screenId: null,
          });
          context.commit("setTempToken", {
            temp_token: data.temp_token,
            expires: data.expires,
            repeat: data.repeat,
            now: data.now,
          });
          return;
        }
        const { temp_token, expires, repeat, now } = data;
        context.commit("setTempToken", { temp_token, expires, repeat, now });
        context.commit("setFormStatus", {
          formName: "recoveryForm",
          code: 1,
        });
        router.push({ name: "CodeRecovery" });
      })
      .finally(() => {
        recoveryStartPromise = null;
      });
    return recoveryStartPromise;
  },
  async pollHybridRecovery(context) {
    const session = context.getters.getHybridRecovery;
    if (!session || !session.tempToken || !session.pollKey) {
      return { recovery_status: null };
    }
    let data;
    try {
      const response = await authRequests.passwordResetStatusRequest({
        temp_token: session.tempToken,
        poll_key: session.pollKey,
      });
      data = response && response.data ? response.data : {};
    } catch {
      // Сеть на poll не пишет фразу и не печатает секрет (D-171, T-11-14)
      return { recovery_status: null };
    }
    const status = data.recovery_status;
    const current = () => context.getters.getHybridRecovery || session;

    if (data.link_rotated && hasRecoverySecrets(data)) {
      context.commit("setHybridRecovery", swapRecoverySecrets(current(), data));
    }

    if (status === "replaced" && hasRecoverySecrets(data)) {
      context.commit("setHybridRecovery", swapRecoverySecrets(current(), data));
      return {
        recovery_status: "replaced",
        link_reopen: Boolean(data.link_reopen),
        leaveToStart: false,
      };
    }

    if (
      status === "replaced" ||
      status === "expired" ||
      status === "cancelled"
    ) {
      // Пароль не пишем и confirm не вызываем (D-179, D-196, D-200)
      context.commit("setHybridRecovery", leaveHybridRecovery(current()));
      router.push({ name: "StartRecovery" });
      return {
        recovery_status: status,
        leaveToStart: true,
        phrase: RECOVERY_DEAD_PHRASE,
      };
    }

    if (status === "verified") {
      const live = current();
      context.commit("setHybridRecovery", {
        ...live,
        step: "password",
        screenId: live.screenId || newRecoveryScreenId(),
        openedAt: live.openedAt,
      });
      context.commit("setFormStatus", {
        formName: "recoveryForm",
        code: 2,
      });
      return { recovery_status: "verified", step: "password" };
    }

    return {
      recovery_status: status || null,
      link_reopen: Boolean(data.link_reopen),
      leaveToStart: false,
    };
  },
  async cancelHybridRecovery(context) {
    const session = context.getters.getHybridRecovery;
    if (!session || !session.tempToken || !session.pollKey) {
      return;
    }
    try {
      await authRequests.passwordResetCancelRequest({
        temp_token: session.tempToken,
        poll_key: session.pollKey,
      });
    } catch {
      // Отмена best-effort: ошибка сети не блокирует возврат на форму
    }
  },
  async sendHybridRecoverySms(context) {
    const session = context.getters.getHybridRecovery;
    if (!session || !session.tempToken || !session.pollKey) {
      return null;
    }
    return authRequests.passwordResetSendCodeRequest({
      temp_token: session.tempToken,
      poll_key: session.pollKey,
    });
  },
  async confirmHybridRecovery(context, payload) {
    const session = context.getters.getHybridRecovery || {};
    const password = payload && payload.password;
    const code = payload && payload.code;
    try {
      const response = await authRequests.passwordResetConfirmRequest({
        temp_token: session.tempToken,
        code,
        password,
        screen_id: session.screenId,
      });
      return { ok: true, data: response.data };
    } catch (error) {
      const body = error && error.response && error.response.data;
      const errorCode = body && body.error_code;
      if (
        errorCode === "password_already_saved" ||
        errorCode === "attempt_consumed"
      ) {
        return { ok: false, error_code: errorCode };
      }
      throw error;
    }
  },
  async resolveRecoveryLink(context, code) {
    const response = await authRequests.passwordResetLinkRequest(code);
    const data = response && response.data ? response.data : {};
    if (data.state === "password" && hasRecoverySecrets(data)) {
      const existing = context.getters.getHybridRecovery || {};
      context.commit("setHybridRecovery", {
        phone: existing.phone || "",
        tempToken: data.temp_token,
        pollKey: data.poll_key,
        maxBotDeeplink: existing.maxBotDeeplink || null,
        maxBotWebDeeplink: existing.maxBotWebDeeplink || null,
        smsAvailableAt: existing.smsAvailableAt || null,
        openedAt: existing.openedAt || null,
        step: "password",
        screenId: existing.screenId || newRecoveryScreenId(),
      });
      context.commit("setTempToken", {
        temp_token: data.temp_token,
        expires: null,
        repeat: null,
        now: null,
      });
      context.commit("setFormStatus", {
        formName: "recoveryForm",
        code: 2,
      });
    }
    return data;
  },
  async checkRecoveryCode(context) {
    const recoveryForm = context.getters.getRecoveryForm;
    return new Promise((resolve, reject) => {
      if (recoveryForm.status > 0)
        authRequests
          .checkRegistrationCodeRequest({
            temp_token: context.state.tempToken.temp_token,
            code: recoveryForm.code.value,
          })
          // eslint-disable-next-line no-unused-vars
          .then((response) => {
            context.commit("setFormStatus", {
              formName: "recoveryForm",
              code: 2,
            });
            router.push({ name: "FinishRecovery" });
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
    });
  },
  async finishRecovery(context) {
    const recoveryForm = context.getters.getRecoveryForm;
    return new Promise((resolve, reject) => {
      if (recoveryForm.status > 1)
        authRequests
          .passwordResetConfirmRequest({
            temp_token: context.state.tempToken.temp_token,
            code: recoveryForm.code.value,
            password: recoveryForm.password.value,
          })
          .then((response) => {
            const { token, user } = response.data;
            context.commit("setToken", { token: { ...token, user: user.id } });
            context.commit("setUser", { user });
            context.commit("setFormStatus", {
              formName: "recoveryForm",
              code: 0,
            });
            router.push("/");
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
    });
  },
  // async startRegistration(context, { type, captcha }) {
  //   try {
  //     const phone = context.state.registrationForm.phone.value.replace(
  //       /[^\d+]/g,
  //       ""
  //     );
  //
  //     // Добавляем в запрос капчу
  //     const response = await authRequests.registrationRequest({
  //       phone,
  //       code_type: type ? type.type : "phone_numbers",
  //       captcha
  //     });
  //
  //     if (response.data) {
  //       const { temp_token, expires, repeat, now } = response.data;
  //
  //       // Обновляем состояние приложения
  //       context.commit("setTempToken", { temp_token, expires, repeat, now });
  //       context.commit("setFormStatus", {
  //         formName: "registrationForm",
  //         code: 5
  //       });
  //
  //       // Перенаправляем пользователя на страницу "CodeRegistration"
  //       router.push({ name: "CodeRegistration" });
  //     }
  //
  //     return response; // Возвращаем ответ от сервера
  //   } catch (error) {
  //     if (error.response && error.response.data.code === "users-05") {
  //       context.commit("setFormStatus", {
  //         formName: "registrationForm",
  //         code: 1
  //       });
  //       router.push({ name: "CityRegistration" });
  //     }
  //
  //     throw error;
  //   }
  // },
  async startRegistration(context, type) {
    if (!context.state.registrationSessionId) {
      const sessionId = generateUUID();
      context.commit("setRegistrationSessionId", sessionId);
    }

    return new Promise((resolve, reject) => {
      const registrationPayload = {
        phone: context.state.registrationForm.phone.value.replace(
          /[^\d+]/g,
          "",
        ),
        code_type: type ? type.type : "phone_numbers",
        supports_max_hybrid: true,
      };
      if (!devSkipCaptcha) {
        registrationPayload.captcha = context.getters.getCaptchaValue.value;
      }
      authRequests
        .registrationRequest(registrationPayload)
        .then((response) => {
          if (response.data) {
            const {
              temp_token,
              expires,
              repeat,
              now,
              verification_mode,
              max_bind_token,
              max_bot_deeplink,
              max_bot_web_deeplink,
              sms_available_at,
            } = response.data;
            context.commit("setTempToken", {
              temp_token,
              expires,
              repeat,
              now,
            });

            if (verification_mode === "max_hybrid" && max_bot_deeplink) {
              context.commit("setHybridRegistration", {
                verification_mode,
                max_bind_token,
                max_bot_deeplink,
                max_bot_web_deeplink,
                sms_available_at,
                // Token A для poll — не затирается SMS token B
                poll_temp_token: temp_token,
              });
            } else {
              context.commit("clearHybridRegistration");
            }

            context.commit("setFormStatus", {
              formName: "registrationForm",
              code: 5,
            });
            router.push({ name: "CodeRegistration" });
            resolve();
          }
        })
        .catch((error) => {
          context.dispatch("fetchCaptcha");
          if (error.response) {
            if (error.response.data.code === "users-05") {
              context.commit("setFormStatus", {
                formName: "registrationForm",
                code: 1,
              });
              router.push({ name: "CityRegistration" });
              resolve();
            }
          }
          reject(error);
        });
    });
  },
  async selectCity(context, { id }) {
    context.commit("setFormValue", {
      formName: "registrationForm",
      field: "city",
      value: id,
    });
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 2,
    });
    router.push({ name: "ObjectRegistration" });
  },
  async selectObjectAddress(context, { id }) {
    context.commit("setFormValue", {
      formName: "registrationForm",
      field: "object",
      value: id,
    });
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 3,
    });
    router.push({ name: "AccountRegistration" });
  },
  async checkAccount(context) {
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 4,
    });
    router.push({ name: "CheckPhoneRegistration" });
  },
  async registrationByAccount(context) {
    const registrationForm = context.getters.getRegistrationForm;
    return new Promise((resolve, reject) => {
      authRequests
        .registrationByAccountRequest({
          object_id: registrationForm.object.value,
          account: registrationForm.account.value.replace(/[^\d]/g, ""),
          phone: registrationForm.phone.value
            .replace(/[^\d+]/g, "")
            .slice(0, 13),
          code_type: "phone_numbers",
        })
        .then((response) => {
          const {
            temp_token,
            expires,
            repeat,
            now,
            verification_mode,
            max_bind_token,
            max_bot_deeplink,
            max_bot_web_deeplink,
            sms_available_at,
          } = response.data;
          context.commit("setTempToken", { temp_token, expires, repeat, now });

          if (verification_mode === "max_hybrid" && max_bot_deeplink) {
            context.commit("setHybridRegistration", {
              verification_mode,
              max_bind_token,
              max_bot_deeplink,
              max_bot_web_deeplink,
              sms_available_at,
              // Token A для poll — не затирается SMS token B
              poll_temp_token: temp_token,
            });
          } else {
            context.commit("clearHybridRegistration");
          }

          context.commit("setFormStatus", {
            formName: "registrationForm",
            code: 5,
          });
          router.push({ name: "CodeRegistration" });
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async sendHybridConfirmationCode(context) {
    const phone = context.state.registrationForm.phone.value
      .replace(/[^\d+]/g, "")
      .slice(0, 13);
    // Dual-token: B → tempToken для confirm; hybridRegistration + poll_temp_token A не трогаем
    const response = await authRequests.registrationSendConfirmationCodeRequest(
      {
        phone,
      },
    );
    const { temp_token, expires, repeat, now } = response.data;
    context.commit("setTempToken", { temp_token, expires, repeat, now });
    return response;
  },
  async checkRegistrationCode(context) {
    return new Promise((resolve, reject) => {
      authRequests
        .registrationConfirmRegistrationCodeRequest({
          temp_token: context.state.tempToken.temp_token,
          code: context.getters.getRegistrationForm.code.value,
        })
        .then((response) => {
          const { temp_token, expires, repeat, now } = response.data;
          context.commit("setTempToken", { temp_token, expires, repeat, now });
          context.commit("setFormStatus", {
            formName: "registrationForm",
            code: 6,
          });
          router.push({ name: "FinishRegistration" });
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async resumeRegistrationAfterMaxVerify(context, pollData) {
    const resumeToken = extractResumeTokenFromPollData(pollData);
    // Без extractable token не мутируем форму и не пушим FinishRegistration (CR-03)
    if (!resumeToken) {
      return false;
    }
    const { expires, repeat, now } = pollData;
    context.commit("setTempToken", {
      temp_token: resumeToken,
      expires: expires ?? null,
      repeat: repeat ?? null,
      now: now ?? null,
    });
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 6,
    });
    // Флаг и session blob живут до complete/login/logout/expired (D-106 / RES-01)
    context.commit("setHybridPollAwaitingResumeLink", false);
    context.commit("setHybridRegistrationExpired", false);
    router.push({ name: "FinishRegistration" });
    return true;
  },
  async handleHybridRegistrationExpired(context) {
    context.commit("clearHybridRegistration");
    context.commit("setTempToken", null);
    context.commit("setHybridPollAwaitingResumeLink", false);
    context.commit("setHybridRegistrationExpired", false);
    context.commit("setFormValue", {
      formName: "registrationForm",
      field: "code",
      value: "",
    });
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 0,
    });
    router.push({ name: "StartRegistration" });
  },
  async pollRegistrationStatus(context) {
    const tempToken = context.getters.getTempToken;
    const hybrid = context.getters.getHybridRegistration;
    // После SMS confirm tempToken может быть B — poll всегда на исходном A
    const pollTempToken = hybrid?.poll_temp_token || tempToken?.temp_token;
    if (!pollTempToken || !hybrid?.max_bind_token) {
      return { stopPoll: false };
    }

    try {
      const response = await authRequests.registrationStatusRequest({
        temp_token: pollTempToken,
        bind_token: hybrid.max_bind_token,
      });
      const registrationStatus = response.data.registration_status;

      if (registrationStatus === "pending") {
        return { stopPoll: false };
      }
      if (registrationStatus === "verified") {
        // stopPoll только после успешного resume → FinishRegistration (CR-03)
        const resumed = await context.dispatch(
          "resumeRegistrationAfterMaxVerify",
          response.data,
        );
        return { stopPoll: resumed === true };
      }
      if (registrationStatus === "expired") {
        context.commit("setHybridRegistrationExpired", true);
        return { stopPoll: true };
      }
    } catch (error) {
      if (error.response?.status === 403) {
        context.commit("setHybridPollAwaitingResumeLink", true);
        return { stopPoll: true };
      }
    }
    return { stopPoll: false };
  },
  async handleRegistrationResume(context, { resumeToken }) {
    const route = decideResumeRoute(
      context.getters.isAuthorized,
      readHybridFlag(),
    );
    // Stay раньше length-check: иначе невалидный токен утащит залогиненного на StartRegistration
    if (route === "stay") {
      return;
    }

    if (
      !resumeToken ||
      resumeToken.length < RESUME_TOKEN_MIN_LEN ||
      resumeToken.length > RESUME_TOKEN_MAX_LEN
    ) {
      await context.dispatch("handleHybridRegistrationExpired");
      return;
    }

    if (route === "welcome") {
      router.replace({ name: "StartRegistration" });
      return;
    }

    context.commit("setTempToken", {
      temp_token: resumeToken,
      expires: null,
      repeat: null,
      now: null,
    });
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 6,
    });
    router.replace({ name: "FinishRegistration" });
  },
  /**
   * Cold F5 без resume_token: rehydrate blob → poll → password/waiting/start (D-106).
   * Точка входа — App.vue onMounted; token path остаётся у RegistrationResumePage (D-107).
   */
  async coldHybridResumeIfNeeded(context) {
    if (context.getters.isAuthorized) {
      return;
    }

    const currentRoute = router.currentRoute.value;
    const resumeTokenInUrl = currentRoute?.query?.resume_token;
    if (resumeTokenInUrl) {
      // D-107: handleRegistrationResume на RegistrationResumePage
      return;
    }

    if (!readHybridFlag()) {
      return;
    }

    const session = readHybridSession();
    if (!session?.poll_temp_token || !session?.max_bind_token) {
      return;
    }

    // Rehydrate Vuex из диска без повторной записи blob
    context.commit("setHybridRegistration", {
      verification_mode: session.verification_mode || "max_hybrid",
      max_bind_token: session.max_bind_token,
      max_bot_deeplink: session.max_bot_deeplink || null,
      max_bot_web_deeplink: session.max_bot_web_deeplink || null,
      sms_available_at: session.sms_available_at || null,
      poll_temp_token: session.poll_temp_token,
      skipPersist: true,
    });

    // Provisional waiting: guard CodeRegistration требует status >= 5
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 5,
    });

    await context.dispatch("pollRegistrationStatus");

    // verified уже сделал setFormStatus(6) + FinishRegistration
    if (context.state.registrationForm.status >= 6) {
      return;
    }

    // expired → start + clear (D-110)
    if (context.state.hybridRegistrationExpired) {
      await context.dispatch("handleHybridRegistrationExpired");
      return;
    }

    // pending или soft-fail сети/403: ordinary waiting UI, флаг и blob живы (D-108)
    context.commit("setFormStatus", {
      formName: "registrationForm",
      code: 5,
    });
    if (currentRoute?.name !== "CodeRegistration") {
      router.push({ name: "CodeRegistration" });
    }
  },
  async finishRegistration(context) {
    return new Promise((resolve, reject) => {
      authRequests
        .registrationCompleteRequest({
          temp_token: context.getters.getTempToken.temp_token,
          password: context.getters.getRegistrationForm.password.value,
        })
        .then((response) => {
          const { token, user } = response.data;
          context.commit("setToken", { token: { ...token, user: user.id } });
          context.commit("setUser", { user });
          context.commit("clearHybridRegistration");
          context.commit("setFormStatus", {
            formName: "registrationForm",
            code: 0,
          });
          context.commit("setRegistrationSessionId", null);
          router.push("/");
          resolve();
        })
        .catch((error) => {
          const status = error.response?.status;
          const code = error.response?.data?.code;
          // Мёртвый attempt: 401 users-03 / phone_already_confirmed → leave + clear hybrid
          // Policy stay: 400 / code 1000 — только reject, флаг hybrid не трогаем (D-111 / D-128)
          if (
            status === 401 &&
            (code === "users-03" || code === "phone_already_confirmed")
          ) {
            context
              .dispatch("handleHybridRegistrationExpired")
              .finally(() => reject(error));
            return;
          }
          reject(error);
        });
    });
  },
  async logout(context) {
    return new Promise(() => {
      authRequests
        .logoutRequest({ userId: context.getters.getUserId })
        .finally(() => {
          context.commit("logout");
        });
    });
  },
  async fetchCurrentUser(context) {
    return new Promise((resolve, reject) => {
      const currentUserId = context.getters.getUserId;
      getUserByIdRequest({ id: currentUserId })
        .then((res) => {
          context.commit("setUser", { user: res.data });
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
};

const mutations = {
  setCaptcha: (state, captchaUrl) => {
    state.captchaUrl = captchaUrl;
  },
  setCaptchaValue: (state, captcha) => {
    state.captcha = captcha;
  },
  setFormStatus(state, { formName, code }) {
    state[formName].status = code;
  },
  setTempToken(state, payload) {
    // Null/empty payload: очистка без деструктуризации (D-110 / expired → StartRegistration)
    if (payload == null || typeof payload !== "object") {
      state.tempToken = null;
      return;
    }
    const { temp_token, expires, repeat, now } = payload;
    state.tempToken = { temp_token, expires, repeat, now };
  },
  setHybridRegistration(
    state,
    {
      verification_mode,
      max_bind_token,
      max_bot_deeplink,
      max_bot_web_deeplink,
      sms_available_at,
      poll_temp_token,
      skipPersist,
    },
  ) {
    state.hybridRegistration = {
      verification_mode,
      max_bind_token,
      max_bot_deeplink,
      max_bot_web_deeplink: max_bot_web_deeplink || null,
      sms_available_at,
      poll_temp_token: poll_temp_token || null,
    };
    // Cold rehydrate: только RAM, диск уже содержит blob
    if (skipPersist) {
      return;
    }
    writeHybridFlag();
    // Blob для cold poll после F5: poll A + bind (+ deeplink/sms для waiting UI)
    writeHybridSession({
      poll_temp_token: poll_temp_token || null,
      max_bind_token,
      max_bot_deeplink,
      max_bot_web_deeplink: max_bot_web_deeplink || null,
      sms_available_at,
      verification_mode,
    });
  },
  clearHybridRegistration(state) {
    state.hybridRegistration = null;
    clearHybridFlag();
    clearHybridSession();
  },
  setHybridPollAwaitingResumeLink(state, value) {
    state.hybridPollAwaitingResumeLink = value;
  },
  setHybridRegistrationExpired(state, value) {
    state.hybridRegistrationExpired = value;
  },
  setFormValue(state, { formName, field, value }) {
    state[formName][field].value = value;
  },
  setMaxLoginEnabled(state, value) {
    state.maxLoginEnabled = Boolean(value);
  },
  setHybridRecovery(state, session) {
    state.hybridRecovery = session;
    writeHybridRecoverySession(session);
  },
  setUser(state, { user }) {
    state.currentUser = user;

    if (process.env.NODE_ENV === "production") {
      Sentry.setUser({ id: user.id });
    }
  },
  setToken(state, { token }) {
    Axios.defaults.headers["Authorization"] = `Bearer ${token.access}`;
    state.token = token;
    saveToken(token);
    YMUserIdMetcrik(token.user);
  },
  logout(state) {
    state.hybridRegistration = null;
    clearHybridFlag();
    clearHybridSession();
    state.currentUser = null;
    state.token = null;
    removeRegistrationSessionId();
    removeToken();
    Axios.defaults.headers["Authorization"] = "";
    router.replace({ name: "Login" }).catch(() => {});
    if (process.env.NODE_ENV === "production") {
      Sentry.setUser(null);
    }
  },
  setRegistrationSessionId(state, sessionId) {
    state.registrationSessionId = sessionId;
    saveRegistrationSessionId(sessionId);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
